import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { propsFundoModal } from '../../../src/lib/backdropModal';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { logUsageEvent } from '../../../src/lib/logger';
import { RateioAjusteTipo, RateioAjusteUc } from '../../../types';
import { criarAjuste } from './saldosUcsDados';
import { formatarPercentRateio, mesSeguinte, observacaoReinclusao, TIPOS_AJUSTE, type LinhaSaldoUc, type UgPainel } from './saldosUcs';

interface RegistrarAjusteModalProps {
  linha: LinhaSaldoUc;
  ugs: UgPainel[];
  mesRateio: string;
  onClose: () => void;
  onSalvo: (ajuste: RateioAjusteUc) => void;
}

const MOTIVO_MINIMO_TROCA = 10;

/** "0,07" (em %) → 0.0007 (fração da usina), arredondado em 4 casas como a planilha. */
function lerPercent(texto: string): number | null {
  const limpo = texto.trim().replace('%', '').replace(',', '.');
  if (!limpo) return null;
  const n = Number(limpo);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return Math.round(n * 100) / 10000;
}

const paraTextoPercent = (fracao: number | null | undefined) =>
  fracao == null ? '' : (fracao * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const RegistrarAjusteModal: React.FC<RegistrarAjusteModalProps> = ({ linha, ugs, mesRateio, onClose, onSalvo }) => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();

  const sugerido = linha.sugestao?.percent ?? null;
  // Aberto a partir do aviso de saldo acabando: o pedido já vem como "Incluir no rateio", com o porquê escrito.
  const reincluir = linha.divergencias.includes('reincluir_no_rateio');
  const [tipo, setTipo] = useState<RateioAjusteTipo>(linha.recebe ? 'alterar_percentual' : reincluir ? 'incluir' : 'outro');
  const [percentTexto, setPercentTexto] = useState(paraTextoPercent(sugerido ?? linha.percentVigente));
  const [ugNova, setUgNova] = useState('');
  const [ugRemanejamento, setUgRemanejamento] = useState('');
  const [motivo, setMotivo] = useState(reincluir ? observacaoReinclusao(linha) : '');
  const proximo = mesSeguinte(mesRateio);
  const opcoesMes = useMemo(() => {
    const lista: string[] = [];
    let m: string | null = mesRateio;
    for (let i = 0; i < 4 && m; i++) {
      m = mesSeguinte(m);
      if (m) lista.push(m);
    }
    return lista;
  }, [mesRateio]);
  const [mesAlvo, setMesAlvo] = useState(proximo || '');
  const [salvando, setSalvando] = useState(false);

  // Usinas identificadas pelo número que a planilha usa (o antigo), para o ajuste
  // ser conferido contra o próximo rateio sem tradução. Um número aparece uma vez
  // só, pela mesma regra de `resolverGeradora`: "Usina Morrinhos" foi cadastrada
  // com o número antigo da Vera Cruz 03, e o número antigo explícito ganha.
  const opcoesUg = useMemo(() => {
    const vistos = new Set<string>();
    return [...ugs]
      .sort((a, b) => Number(!a.numero_uc_geradora_antigo) - Number(!b.numero_uc_geradora_antigo))
      .map(u => ({ numero: (u.numero_uc_geradora_antigo || u.numero_uc_geradora || '').replace(/\D/g, ''), nome: u.nome }))
      .filter(u => u.numero && !vistos.has(u.numero) && vistos.add(u.numero))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [ugs]);

  const percentNovo = lerPercent(percentTexto);
  const erros: string[] = [];
  if (tipo === 'alterar_percentual' && percentNovo == null) erros.push('Informe o novo % (ex.: 0,07).');
  if (tipo === 'trocar_geradora') {
    if (!ugNova) erros.push('Escolha a nova geradora.');
    else if (ugNova === linha.ugNumero) erros.push('A nova geradora é a mesma de hoje.');
    if (motivo.trim().length < MOTIVO_MINIMO_TROCA) erros.push(`Troca de geradora precisa de motivo (mínimo ${MOTIVO_MINIMO_TROCA} caracteres).`);
  }
  if (tipo === 'remanejar_saldo' && !ugRemanejamento) erros.push('Escolha a usina de onde o saldo será remanejado.');
  if ((tipo === 'outro' || tipo === 'excluir' || tipo === 'incluir') && !motivo.trim()) erros.push('Descreva o ajuste na observação.');

  const salvar = async () => {
    if (erros.length > 0 || salvando) return;
    setSalvando(true);
    try {
      const ajuste = await criarAjuste({
        unidade_consumidora_id: linha.ucId,
        uc_numero: linha.ucNumero,
        tipo,
        percent_atual: linha.percentVigente,
        percent_novo: tipo === 'alterar_percentual' ? percentNovo : null,
        ug_atual: linha.ugNumero,
        ug_nova: tipo === 'trocar_geradora' ? ugNova : null,
        ug_remanejamento: tipo === 'remanejar_saldo' ? ugRemanejamento : null,
        mes_rateio_origem: mesRateio,
        mes_rateio_alvo: mesAlvo || null,
        motivo: motivo.trim() || null,
        criado_por: user?.id ?? null,
        criado_por_nome: profile?.full_name ?? null,
      });
      logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_saldos', acao: 'registrar_ajuste', origem: 'acao' });
      addToast('Ajuste registrado. O rateio só muda quando a planilha do próximo mês subir com ele.', 'success');
      onSalvo(ajuste);
    } catch (error: any) {
      console.error('Erro ao registrar ajuste', error);
      addToast('Não foi possível registrar o ajuste: ' + (error?.message || 'erro desconhecido'), 'error');
    } finally {
      setSalvando(false);
    }
  };

  const tiposDisponiveis: RateioAjusteTipo[] = linha.recebe
    ? ['alterar_percentual', 'remanejar_saldo', 'trocar_geradora', 'excluir', 'outro']
    : ['incluir', 'remanejar_saldo', 'outro'];

  const campo = 'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent';

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" {...propsFundoModal(onClose)}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins">Registrar ajuste</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              UC {linha.ucNumero}{linha.nome ? ` · ${linha.nome}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full" aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">O que precisa mudar</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Tipo de ajuste">
              {tiposDisponiveis.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTipo(t)}
                  aria-pressed={tipo === t}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    tipo === t
                      ? 'bg-primary/10 border-primary/30 text-primary'
                      : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                  }`}
                >
                  {TIPOS_AJUSTE[t]}
                </button>
              ))}
            </div>
          </div>

          {tipo === 'alterar_percentual' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">% atual</p>
                <p className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-900 text-sm font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
                  {formatarPercentRateio(linha.percentVigente)}
                </p>
              </div>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Novo % da usina</span>
                <div className="relative mt-1">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={percentTexto}
                    onChange={e => setPercentTexto(e.target.value.replace(/[^\d.,]/g, ''))}
                    className={`${campo} pr-8 tabular-nums`}
                    placeholder="0,07"
                  />
                  <span className="absolute right-3 top-2 text-sm text-gray-400">%</span>
                </div>
              </label>
              <p className="col-span-2 text-[11px] text-gray-500 dark:text-gray-400">
                {sugerido != null ? (
                  <>
                    Sugerido pelo modelo da planilha: <strong className="text-gray-700 dark:text-gray-200">{formatarPercentRateio(sugerido)}</strong>.{' '}
                    {percentNovo != null && percentNovo !== sugerido ? (
                      <button type="button" onClick={() => setPercentTexto(paraTextoPercent(sugerido))} className="text-primary font-semibold hover:underline">
                        Usar o sugerido
                      </button>
                    ) : null}
                  </>
                ) : (
                  <>Sem sugestão do modelo: {linha.sugestao?.motivo || 'a UC não entra no cálculo.'}</>
                )}
              </p>
            </div>
          ) : null}

          {tipo === 'trocar_geradora' ? (
            <div className="space-y-2">
              <div className="flex gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 text-xs text-amber-800 dark:text-amber-300">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>Uma UC não deve trocar de geradora com frequência. Registre a troca só em caso específico e explique o motivo.</span>
              </div>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Nova geradora</span>
                <select value={ugNova} onChange={e => setUgNova(e.target.value)} className={`${campo} mt-1`}>
                  <option value="">Selecione…</option>
                  {opcoesUg.map(u => (
                    <option key={u.numero} value={u.numero} disabled={u.numero === linha.ugNumero}>
                      {u.nome} ({u.numero}){u.numero === linha.ugNumero ? ' · atual' : ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          {tipo === 'remanejar_saldo' ? (
            <label className="block">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Usina de onde o saldo será remanejado</span>
              <select value={ugRemanejamento} onChange={e => setUgRemanejamento(e.target.value)} className={`${campo} mt-1`}>
                <option value="">Selecione…</option>
                {opcoesUg.map(u => (
                  <option key={u.numero} value={u.numero}>
                    {u.nome} ({u.numero})
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label className="block">
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Deve valer a partir do rateio de</span>
            <select value={mesAlvo} onChange={e => setMesAlvo(e.target.value)} className={`${campo} mt-1`}>
              {opcoesMes.map(m => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
              Observação{tipo === 'trocar_geradora' ? ' (motivo da troca)' : ''}
            </span>
            <textarea
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              rows={3}
              className={`${campo} mt-1 resize-none`}
              placeholder={tipo === 'trocar_geradora' ? 'Por que esta UC precisa mudar de usina?' : 'Ex.: saldo acima de 3 meses, reduzir até estabilizar.'}
            />
            {tipo === 'trocar_geradora' ? (
              <span className="text-[11px] text-gray-400 tabular-nums">
                {motivo.trim().length}/{MOTIVO_MINIMO_TROCA} caracteres mínimos
              </span>
            ) : null}
          </label>

          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            O ajuste fica registrado como pedido. O % que vale continua sendo o da planilha do Rateio Antigo: quando o rateio de {mesAlvo || 'destino'} subir, o painel confere se ele veio como pedido.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30">
          <p className="text-[11px] text-red-600 dark:text-red-400 min-h-[1rem]">{erros[0] || ''}</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              onClick={salvar}
              disabled={erros.length > 0 || salvando}
              className="px-4 py-2 text-sm font-bold text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {salvando ? <Loader2 size={14} className="animate-spin" /> : null}
              Registrar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RegistrarAjusteModal;
